<template>
    <div class="rounded-xl border border-gray-200 bg-white p-5">
        <h2 class="mb-4 font-semibold text-gray-900">
            {{ t("vue.kiosk_da_recent_events") }}
        </h2>
        <div
            v-if="recentEvents.length === 0"
            class="py-10 text-center text-sm text-gray-400"
        >
            {{ t("vue.kiosk_da_no_events") }}
        </div>
        <ul
            v-else
            class="max-h-[480px] divide-y divide-gray-50 overflow-y-auto"
        >
            <li
                v-for="event in recentEvents"
                :key="event.key"
                class="flex items-center gap-3 py-2.5"
            >
                <span class="w-12 shrink-0 font-mono text-xs text-gray-400">
                    {{ event.time ?? "—" }}
                </span>
                <div
                    class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                    :class="event.iconBg"
                >
                    <component :is="event.icon" class="h-3 w-3 text-white" />
                </div>
                <span class="w-36 shrink-0 text-xs text-gray-500">{{
                    event.typeLabel
                }}</span>
                <span
                    class="min-w-0 flex-1 truncate text-sm font-medium text-gray-900"
                >
                    {{ event.entity }}
                </span>
                <span
                    v-if="event.secondary"
                    class="hidden shrink-0 text-xs text-gray-400 sm:block"
                >
                    {{ event.secondary }}
                </span>
                <AdminLink v-if="event.kioskUrl" :href="event.kioskUrl" />
            </li>
        </ul>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import {
    UserPlusIcon,
    CheckBadgeIcon,
    UserGroupIcon,
    CreditCardIcon,
    ClockIcon,
    CalendarIcon,
    ArrowPathIcon,
    CurrencyDollarIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import type {
    IRecentEvent,
    ITeamItem,
    IUserItem,
    IYearlySubItem,
} from "../types";

const { t } = useTranslations();

const props = defineProps<{
    newUsers: IUserItem[];
    verifiedUsers: IUserItem[];
    newTeams: ITeamItem[];
    newFreePlans: ITeamItem[];
    freePlanRestarts: ITeamItem[];
    newSubscriptions: ITeamItem[];
    endingTrials: ITeamItem[];
    yearlySubscriptions: IYearlySubItem[];
}>();

const recentEvents = computed((): IRecentEvent[] => {
    const events: IRecentEvent[] = [];

    props.newUsers.forEach((u) => {
        events.push({
            key: `reg-${u.id}`,
            time: u.time,
            typeLabel: t("vue.kiosk_da_event_registration"),
            entity: u.name,
            secondary: u.email,
            icon: UserPlusIcon,
            iconBg: "bg-blue-500",
            kioskUrl: u.kiosk_url,
        });
    });

    props.verifiedUsers.forEach((u) => {
        events.push({
            key: `ver-${u.id}`,
            time: u.time,
            typeLabel: t("vue.kiosk_da_event_email_verification"),
            entity: u.email,
            secondary: u.name,
            icon: CheckBadgeIcon,
            iconBg: "bg-green-500",
            kioskUrl: u.kiosk_url,
        });
    });

    props.newTeams.forEach((t_) => {
        events.push({
            key: `team-${t_.id}`,
            time: t_.time,
            typeLabel: t("vue.kiosk_da_event_new_team"),
            entity: t_.name,
            secondary: t_.owner_email,
            icon: UserGroupIcon,
            iconBg: "bg-purple-500",
            kioskUrl: t_.kiosk_url,
        });
    });

    props.newFreePlans.forEach((t_) => {
        events.push({
            key: `fp-${t_.id}`,
            time: t_.time,
            typeLabel: t("vue.kiosk_da_event_free_plan"),
            entity: t_.name,
            secondary: t_.owner_email,
            icon: CreditCardIcon,
            iconBg: "bg-orange-500",
            kioskUrl: t_.kiosk_url,
        });
    });

    props.freePlanRestarts.forEach((t_) => {
        events.push({
            key: `fpr-${t_.id}`,
            time: t_.time,
            typeLabel: t("vue.kiosk_da_event_free_restart"),
            entity: t_.name,
            secondary: t_.owner_email,
            icon: ArrowPathIcon,
            iconBg: "bg-amber-500",
            kioskUrl: t_.kiosk_url,
        });
    });

    props.newSubscriptions.forEach((t_) => {
        events.push({
            key: `sub-${t_.id}`,
            time: t_.time,
            typeLabel: t("vue.kiosk_da_event_subscription"),
            entity: t_.name,
            secondary: t_.owner_email,
            icon: CurrencyDollarIcon,
            iconBg: "bg-emerald-600",
            kioskUrl: t_.kiosk_url,
        });
    });

    props.endingTrials.forEach((t_) => {
        events.push({
            key: `trial-${t_.id}`,
            time: t_.time,
            typeLabel: t("vue.kiosk_da_event_trial_ending"),
            entity: t_.name,
            secondary: t_.owner_email,
            icon: ClockIcon,
            iconBg: "bg-red-500",
            kioskUrl: t_.kiosk_url,
        });
    });

    props.yearlySubscriptions.forEach((s) => {
        events.push({
            key: `ysub-${s.id}`,
            time: s.subscription_start_at,
            typeLabel: t("vue.kiosk_da_event_yearly_sub"),
            entity: s.team_name,
            secondary: s.owner_email,
            icon: CalendarIcon,
            iconBg: "bg-teal-600",
            kioskUrl: s.kiosk_url,
        });
    });

    return events
        .sort((a, b) => (b.time ?? "").localeCompare(a.time ?? ""))
        .slice(0, 120);
});
</script>
